'use client'

// SUMMARY: In-chat card for a downloadable DATA export (xlsx / csv), rendered for ?kind=data document links.
// Unlike the PDF document card it (a) downloads the RAW stored bytes via the byte route (never the PDF
// re-render path) and (b) previews the actual table in a dialog via /api/ai/documents/[id]/preview. Keeps
// spreadsheet exports out of the PDF editor entirely.

import { useState } from 'react'
import { useTranslations } from 'next-intl'
import { Download, Table2, Loader2 } from 'lucide-react'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'

// A leading file emoji the model may prepend to the link text (📄/📝/📑/📎).
const FILE_EMOJI = /^\s*[\u{1F4C4}\u{1F4DD}\u{1F4D1}\u{1F4CE}]\s*/u
// The document id inside the href (/api/ai/documents/<id>?kind=data).
const DOC_ID = /\/documents\/([^/?]+)/

interface PreviewSheet {
	name: string
	columns: string[]
	rows: Record<string, string | number | boolean | null>[]
	rowCount: number
}

export function DataFileCard({ href, label }: { href: string; label: string }) {
	const t = useTranslations('projects.files')
	const [open, setOpen] = useState(false)
	const [sheets, setSheets] = useState<PreviewSheet[] | null>(null)
	const [loading, setLoading] = useState(false)
	const [failed, setFailed] = useState(false)

	const filename = label.replace(FILE_EMOJI, '').trim() || label
	const docId = href.match(DOC_ID)?.[1] ?? ''
	const downloadHref = docId ? `/api/ai/documents/${docId}` : href // raw bytes, correct mimeType + filename

	async function openPreview() {
		setOpen(true)
		if (sheets || loading) return
		setLoading(true)
		setFailed(false)
		try {
			const res = await fetch(`/api/ai/documents/${docId}/preview`)
			if (!res.ok) throw new Error()
			const data = await res.json()
			setSheets(Array.isArray(data.sheets) ? data.sheets : [])
		} catch {
			setFailed(true)
		} finally {
			setLoading(false)
		}
	}

	return (
		<span className="my-2 inline-flex max-w-full items-center gap-1 rounded-lg border border-border bg-foreground/[0.03] p-1 pl-3 align-middle">
			<button type="button" onClick={openPreview} className="flex min-w-0 items-center gap-3 py-1.5 pr-1 text-left">
				<Table2 className="h-5 w-5 shrink-0 text-primary" />
				<span className="flex min-w-0 flex-col">
					<span className="truncate text-sm font-medium text-foreground">{filename}</span>
					<span className="text-xs text-muted-foreground">{t('preview')}</span>
				</span>
			</button>
			<a
				href={downloadHref}
				download
				title={t('download')}
				aria-label={t('download')}
				className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-foreground/[0.06] hover:text-foreground sm:h-8 sm:w-8"
			>
				<Download className="h-4 w-4" />
			</a>

			<Dialog open={open} onOpenChange={setOpen}>
				<DialogContent className="flex max-h-[88vh] w-[min(calc(100vw-2rem),1000px)] max-w-none flex-col gap-3 overflow-hidden sm:max-w-[min(calc(100vw-2rem),1000px)]">
					<DialogHeader>
						<DialogTitle className="truncate">{filename}</DialogTitle>
						<DialogDescription className="sr-only">{t('preview')}</DialogDescription>
					</DialogHeader>
					<div className="min-h-0 flex-1 overflow-auto">
						{loading && (
							<div className="flex items-center justify-center gap-2 py-10 text-sm text-muted-foreground">
								<Loader2 className="h-4 w-4 animate-spin" />
							</div>
						)}
						{failed && <p className="py-10 text-center text-sm text-muted-foreground">{t('previewFailed')}</p>}
						{sheets?.map((sheet) => (
							<SheetTable key={sheet.name} sheet={sheet} t={t} />
						))}
						{sheets && sheets.length === 0 && (
							<p className="py-10 text-center text-sm text-muted-foreground">{t('previewEmpty')}</p>
						)}
					</div>
				</DialogContent>
			</Dialog>
		</span>
	)
}

function SheetTable({ sheet, t }: { sheet: PreviewSheet; t: ReturnType<typeof useTranslations<'projects.files'>> }) {
	return (
		<div className="mb-5">
			<p className="mb-1.5 text-xs font-semibold text-muted-foreground">
				{sheet.name} · {sheet.rowCount}
			</p>
			<div className="overflow-x-auto rounded-md border border-border">
				<table className="w-full border-collapse text-xs">
					<thead>
						<tr className="bg-muted/50">
							{sheet.columns.map((col) => (
								<th key={col} className="whitespace-nowrap border-b border-border px-2 py-1.5 text-left font-semibold">
									{col}
								</th>
							))}
						</tr>
					</thead>
					<tbody>
						{sheet.rows.map((row, i) => (
							<tr key={i} className="odd:bg-foreground/[0.015]">
								{sheet.columns.map((col) => (
									<td key={col} className="max-w-[240px] truncate border-b border-border/60 px-2 py-1 tabular-nums">
										{cell(row[col])}
									</td>
								))}
							</tr>
						))}
					</tbody>
				</table>
			</div>
			{sheet.rowCount > sheet.rows.length && (
				<p className="mt-1.5 text-[11px] text-muted-foreground">
					{t('previewNote', { shown: sheet.rows.length, total: sheet.rowCount })}
				</p>
			)}
		</div>
	)
}

function cell(value: string | number | boolean | null): string {
	if (value === null || value === undefined) return ''
	return String(value)
}
